/**
 * Daily Rhythm API client.
 *
 * Lives here rather than in api/client.js for the same reason growthApi and
 * encountersApi do: the feature carries upload and formatting helpers that only
 * it uses, and client.js is already long.
 */
import { api } from '../../api/client';
// Reused rather than reimplemented — a second uploader would be a second set of
// size limits and a second place to forget stripSignature.
export { stripSignature, uploadPlanImage as uploadRhythmImage,
         uploadPlanVideo as uploadRhythmVideo } from '../courses/growthApi';

export const rhythmApi = {
  // Settings
  getSettings:    (communityId)        => api.get(`/api/daily-rhythm/settings/${communityId}`),
  updateSettings: (communityId, data)  => api.put(`/api/daily-rhythm/settings/${communityId}`, data),

  // Programs
  listPrograms:  (communityId)             => api.get(`/api/daily-rhythm/programs/${communityId}`),
  createProgram: (communityId, data)       => api.post(`/api/daily-rhythm/programs/${communityId}`, data),
  getProgram:    (communityId, programId)  => api.get(`/api/daily-rhythm/programs/${communityId}/${programId}`),
  updateProgram: (communityId, programId, data) =>
    api.put(`/api/daily-rhythm/programs/${communityId}/${programId}`, data),
  publishProgram: (communityId, programId) =>
    api.post(`/api/daily-rhythm/programs/${communityId}/${programId}/publish`),
  archiveProgram: (communityId, programId) =>
    api.post(`/api/daily-rhythm/programs/${communityId}/${programId}/archive`),
  deleteProgram:  (communityId, programId) =>
    api.delete(`/api/daily-rhythm/programs/${communityId}/${programId}`),

  // Days
  // `src` (push | bell | feed) is how the member arrived, for the organizer's
  // program report. Attribution only — it changes nothing in the response.
  getDay:    (communityId, programId, date, { src } = {}) =>
    api.get(`/api/daily-rhythm/programs/${communityId}/${programId}/days/${date}`
      + (src ? `?src=${encodeURIComponent(src)}` : '')),
  updateDay: (communityId, programId, dayId, data) =>
    api.put(`/api/daily-rhythm/programs/${communityId}/${programId}/days/${dayId}`, data),
  releaseDay: (communityId, programId, dayId, slot) =>
    api.post(`/api/daily-rhythm/programs/${communityId}/${programId}/days/${dayId}/release?slot=${slot}`),

  // Organizer report — aggregate counts only.
  getReport: (communityId, programId) =>
    api.get(`/api/daily-rhythm/programs/${communityId}/${programId}/report`),

  // Today (community home card)
  today: (communityId) => api.get(`/api/daily-rhythm/today/${communityId}`),

  // Reflections
  saveReflection: (dayId, data)  => api.put(`/api/daily-rhythm/days/${dayId}/reflection`, data),
  listShared:     (dayId)        => api.get(`/api/daily-rhythm/days/${dayId}/reflections`),

  // My week — the caller's own reflections across a program, and the private
  // summary that is made only when they ask for it.
  myReflections: (communityId, programId) =>
    api.get(`/api/daily-rhythm/programs/${communityId}/${programId}/my-reflections`),
  mySummary:     (communityId, programId) =>
    api.post(`/api/daily-rhythm/programs/${communityId}/${programId}/my-summary`),
};

// ── Formatting helpers ───────────────────────────────────────────────────────

/**
 * Render an instant as a wall clock IN THE PROGRAM'S TIMEZONE, with the zone
 * abbreviation attached.
 *
 * The abbreviation is not decoration. A program's times are set by an organizer
 * in one zone and read by members who may be in another; a bare "6:00 AM" is
 * ambiguous exactly when it matters most.
 */
export function formatInZone(instant, timezone, opts = {}) {
  if (!instant) return '';
  try {
    return new Intl.DateTimeFormat(undefined, {
      timeZone: timezone || undefined,
      hour: 'numeric', minute: '2-digit',
      timeZoneName: opts.showZone === false ? undefined : 'short',
      ...opts.dateStyleParts,
    }).format(new Date(instant));
  } catch {
    return new Date(instant).toLocaleTimeString();
  }
}

/** "Sat, Sep 27" in the program's zone. */
export function formatDayLabel(dateKey, timezone) {
  if (!dateKey) return '';
  try {
    // A date key is UTC-midnight; format it in UTC so the label never slides a
    // day for anyone west of the program's zone.
    return new Intl.DateTimeFormat(undefined, {
      timeZone: 'UTC', weekday: 'short', month: 'short', day: 'numeric',
    }).format(new Date(`${dateKey}T00:00:00.000Z`));
  } catch { return dateKey; }
}

/**
 * { weekday: "Sun", day: "27" } for the date tile on the program's day list.
 * Formatted in UTC for the same reason as formatDayLabel: a date key is
 * UTC-midnight, and any other zone slides it a day for someone west of it.
 */
export function dayTileParts(dateKey) {
  if (!dateKey) return { weekday: '', day: '' };
  try {
    const d = new Date(`${dateKey}T00:00:00.000Z`);
    if (Number.isNaN(d.getTime())) return { weekday: '', day: '' };
    return {
      weekday: new Intl.DateTimeFormat(undefined, { timeZone: 'UTC', weekday: 'short' }).format(d),
      day:     String(d.getUTCDate()),
    };
  } catch { return { weekday: '', day: '' }; }
}

/** Compact "3h 12m" / "12m" / "40s" countdown. */
export function formatCountdown(ms) {
  if (ms == null || ms <= 0) return '';
  const total = Math.floor(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m`;
  return `${s}s`;
}

/**
 * YouTube / Vimeo → embeddable URL. Ported from WellThreadScreen's toEmbedUrl.
 * Returns null for anything else, and the caller falls back to a plain link.
 */
export function toEmbedUrl(url) {
  if (!url) return null;
  let u;
  try { u = new URL(/^https?:\/\//i.test(url) ? url : `https://${url}`); }
  catch { return null; }
  const host = u.hostname.replace(/^(?:www|m)\./, '');
  if (host === 'youtu.be') {
    const id = u.pathname.slice(1);
    return id ? `https://www.youtube.com/embed/${id}` : null;
  }
  if (host === 'youtube.com') {
    if (u.pathname === '/watch') {
      const id = u.searchParams.get('v');
      return id ? `https://www.youtube.com/embed/${id}` : null;
    }
    if (u.pathname.startsWith('/embed/')) return u.toString();
    if (u.pathname.startsWith('/shorts/')) {
      const id = u.pathname.split('/')[2];
      return id ? `https://www.youtube.com/embed/${id}` : null;
    }
  }
  if (host === 'vimeo.com') {
    const id = u.pathname.split('/').filter(Boolean)[0];
    return /^\d+$/.test(id || '') ? `https://player.vimeo.com/video/${id}` : null;
  }
  return null;
}

/** A short IANA list for the settings picker, plus whatever is already set. */
export const COMMON_TIMEZONES = [
  'America/New_York', 'America/Chicago', 'America/Denver', 'America/Phoenix',
  'America/Los_Angeles', 'America/Anchorage', 'Pacific/Honolulu',
  'America/Toronto', 'America/Mexico_City', 'America/Sao_Paulo',
  'Europe/London', 'Europe/Dublin', 'Europe/Madrid', 'Europe/Berlin',
  'Africa/Lagos', 'Africa/Nairobi', 'Asia/Jerusalem', 'Asia/Kolkata',
  'Asia/Manila', 'Asia/Seoul', 'Asia/Tokyo', 'Australia/Sydney',
  'Pacific/Auckland', 'UTC',
];
